import React, { useState, useEffect, useCallback } from 'react';
import { api, setToken, isAuthed, logout, getToken } from './api';
import CounselSync from './CounselSync';

function LoginScreen() {
  const [username, setUsername] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr('');
    try {
      const res = username
        ? await api.login(pw, username)
        : await api.login(pw);
      setToken(res.token);
      if (res.user) localStorage.setItem('cs_user', JSON.stringify(res.user));
      window.location.reload();
    } catch (e) {
      setErr(e.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
      <form onSubmit={handleLogin} style={{ background: '#fff', padding: 40, borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', width: 360, textAlign: 'center' }}>
        <img src="/logo.png" alt="Midwest ADP - CounselSync" style={{ width: '100%', maxWidth: 280, height: 'auto', display: 'block', margin: '0 auto 18px' }} />
        <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 24px' }}>Sign In</p>
        <input
          type="text"
          value={username}
          onChange={e => setUsername(e.target.value)}
          placeholder="Username"
          style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 14, outline: 'none', marginBottom: 10 }}
          autoFocus
        />
        <input
          type="password"
          value={pw}
          onChange={e => setPw(e.target.value)}
          placeholder="Password"
          style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 14, outline: 'none', marginBottom: 12 }}
        />
        {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{err}</div>}
        <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(isAuthed());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cs_user')); } catch { return null; }
  });

  const loadData = useCallback(async () => {
    try {
      const [counselors, clients, reminderConfig, globalTopics] = await Promise.all([
        api.getCounselors(),
        api.getClients(),
        api.getReminderConfig(),
        api.getGlobalTopics(),
      ]);
      // Also try to get current user info
      let me = user;
      try { me = await api.getMe(); setUser(me); } catch(e) {}
      setData({ counselors, clients, reminderConfig, globalTopics, user: me });
    } catch (e) {
      console.error('Load error:', e);
      if (e.message === 'Unauthorized') { setAuthed(false); logout(); }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authed) loadData();
    else setLoading(false);
  }, [authed, loadData]);

  // Session timeout check — poll every 60s
  useEffect(() => {
    if (!authed) return;
    const check = setInterval(() => {
      const token = getToken();
      if (!token) { setAuthed(false); return; }
      // Try to decode JWT and check expiry
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          logout();
          setAuthed(false);
        }
      } catch (e) { /* legacy token, no expiry check */ }
    }, 60000);
    return () => clearInterval(check);
  }, [authed]);

  if (!authed) return <LoginScreen />;
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        </div>
        <div style={{ color: '#64748b', fontSize: 14 }}>Loading CounselSync...</div>
      </div>
    </div>
  );
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Failed to load data. Check server connection.</div>;

  return <CounselSync initialData={data} api={api} onLogout={() => { logout(); localStorage.removeItem('cs_user'); setAuthed(false); }} />;
}
